import React, { useState, useContext, useEffect, useMemo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Text } from "@foris/avocado-suite";
import { useLocation } from "react-router-dom";
import { Section, Session } from "@models/ISchema";
import { AppContext } from "../../context/EditSessionsContext";
import { Types as TypeResult } from "../../context/result.reducer";
import { Types as TypeSwitch } from "../../context/switchPage.reducer";
import { Types, FormActions, EditedSession } from "../../context/formData.reducer";
import { Types as TypeLink } from "../../context/linkData.reducer";
import { IEvent, IWeek } from "../../../ISections";
import { compare } from "../../utils/compareEdited";
import { ContextEDH } from "@context/ContextEDH";
import { usePastSessionsVisibilityStore } from "../../context/usePastSessionsVisibilityStore";
import useSessionsCrudMutation from "../../hooks/useSessionsCrudMutation";
import { useLoadBlockingThreshold } from "../../hooks/useLoadBlockingThreshold";
import Modal from "@components/modal";
import AccordionSections from "../../components/Accordion/AccordionSections";
import EditSession from "./EditSession";
import CreateSession from "./CreateSession";
import AssignationActionsHeader from "../../components/assignation-header";
import css from "./formEdit.module.scss";

interface IFormEdit {
  heightHeader: number;
}

interface ILocationState {
  currentSelectedWeek?: IWeek;
  sectionId?: string;
  sessionId?: string;
  event?: IEvent;
  createSession?: boolean;
}

const FormEdit: React.FC<IFormEdit> = (props: IFormEdit) => {
  const { heightHeader } = props;
  const location = useLocation();
  const locationState: ILocationState = location.state;
  const currentSelectedWeek = locationState?.currentSelectedWeek;
  const [resultSessionsCrud, submitSessionsCrud] = useSessionsCrudMutation({
    dryRun: true,
    skipValidations: true,
  });
  const { state, dispatch } = useContext(AppContext);
  const { state: stateEDH } = useContext(ContextEDH);
  const resetPastSessionsVisibility = usePastSessionsVisibilityStore(s => s.reset);
  const { loadBlockingThreshold } = useLoadBlockingThreshold();

  useEffect(() => {
    loadBlockingThreshold();

    return () => {
      resetPastSessionsVisibility();
    };
  }, []);
  const [warning, setWarning] = useState(false);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [, setVariables] = useState<FormActions>(null);
  const [error, setError] = useState(false);
  const numberOfSavedSessions = state?.form?.savedSessions?.length ?? 0;
  const numberOfSessionsToCreate = state?.form?.sessionsToCreate?.length ?? 0;
  const numberOfDeletedSessions = useMemo(
    () => Object.keys(state?.form?.sessionsToDelete ?? {}).length,
    [state?.form?.sessionsToDelete],
  );

  const { t } = useTranslation();

  const handleCompareBySection = (
    sessions: Session[],
    section: Section,
    checked: boolean,
    newSessions: EditedSession[],
  ) => {
    const mockSessions = newSessions.map(session => ({
      id: session?.id,
      origin: null,
      vacancies: null,
      events: null,
      ...(session ?? {}),
      section,
    }));

    const cloneSessions = [...mockSessions, ...sessions];
    const payload = {
      checked: !checked,
      sessionsOfSection: cloneSessions,
      sectionId: section.id,
    };

    const allSessionsMatch = cloneSessions.every(session => compare(session, state));

    if (allSessionsMatch) {
      dispatch({ type: Types.SelectedSessionsBySection, payload });
      setWarning(false);
      return !checked;
    } else {
      setWarning(true);
      setVariables({ type: Types.SelectedSessionsBySection, payload });
    }

    return checked;
  };

  const handleCompareSession = (
    session: Session,
    editedSession: EditedSession = null,
    section: Section = null,
  ) => {
    const isClonedSession = (session: EditedSession) => !!session?.isCloned;
    const { selectedCreateSession, editedSessions } = state?.form;

    // Revisa si la sesión a crear tuvo alguna edición y todavía no ha sido
    // guardada (CASO EDITAR)
    if (!selectedCreateSession && session) {
      dispatch({ type: Types.SelectedSessions, payload: session });
      setWarning(false);
    }

    // Si se está seleccionando una nueva sesión ya creada
    if (!selectedCreateSession && editedSession) {
      if (isClonedSession(editedSession)) {
        dispatch({ type: Types.SelectedSessions, payload: editedSession });
        setWarning(false);
      } else {
        const mockSession: Session = {
          id: editedSession?.id,
          origin: null,
          vacancies: null,
          events: null,
          section,
        };

        if (compare(mockSession, state)) {
          dispatch({ type: Types.SelectedSessions, payload: mockSession });
          setWarning(false);
        }
      }
    }

    // Revisa si la nueva sesión tuvo alguna edición (CASO NUEVA SESIÓN)
    if (selectedCreateSession && session) {
      const blocks = !!editedSessions?.blocks?.selected;
      const classrooms = !!editedSessions?.classrooms?.length;
      const instructors = !!editedSessions?.instructors?.length;
      const intervals = !!editedSessions?.intervals?.some(interval => interval?.checked);
      if (blocks || classrooms || instructors || intervals) {
        setWarning(true);
        setVariables({ type: Types.SelectedSessions, payload: session });
      } else {
        dispatch({ type: Types.CleanFormCreateSession });
        dispatch({ type: Types.SelectedSessions, payload: session });
      }
    }
    if (selectedCreateSession && editedSession) {
      const mockSession: Session = {
        id: editedSession?.id,
        origin: null,
        vacancies: null,
        events: null,
        section,
      };
      if (compare(mockSession, state)) {
        dispatch({ type: Types.SelectedSessions, payload: mockSession });
        setWarning(false);
      }
    }
  };

  useEffect(() => {
    if (!!state?.form?.editedSessions?.blocks && locationState) {
      if (currentSelectedWeek && !state.link.selectedWeek) {
        dispatch({ type: TypeLink.SetSelectedWeek, payload: currentSelectedWeek });
      }

      if (!!locationState?.sessionId && !!state?.link?.sections?.length) {
        const { sessionId } = locationState;
        let selectedSession: EditedSession = {};

        state?.link?.sections.forEach((section: Section) => {
          const sessions = section.sessions || [];
          const unasignedSessions = section.unasignedSessions || [];
          const allSessions = [...sessions, ...unasignedSessions];

          allSessions.forEach(session => {
            if (
              !Object.keys(selectedSession).length &&
              !!session?.id &&
              String(session?.id) === String(sessionId)
            ) {
              selectedSession = session;
            }
          });
        });

        if (Object.keys(selectedSession).length && !state?.form?.sessionFromLocationSelected) {
          dispatch({ type: Types.SelectedSessions, payload: selectedSession });
          dispatch({ type: Types.SetSessionFromLocationSelected, payload: true });
        }
      }

      if (
        locationState?.event &&
        locationState?.event?.resource?.blockRange &&
        !!stateEDH?.base?.base?.user?.abilities?.can_assign_blocks
      ) {
        window.dispatchEvent(new Event("enable-edition-blocks"));
        dispatch({
          type: Types.SetIsEditionEnabled,
          payload: {
            day: true,
          },
        });
        dispatch({
          type: Types.SetIsEditionEnabled,
          payload: {
            blocks: true,
          },
        });

        dispatch({
          type: Types.BlocksEditedSessions,
          payload: {
            blocks: {
              ...state?.form?.editedSessions?.blocks,
              startTime: locationState?.event?.resource?.blockRange?.start?.startingTime,
              endTime: locationState?.event?.resource?.blockRange?.end?.endingTime,
              day: locationState?.event?.resource?.blockRange?.start?.day,
              selected: "hours",
            },
          },
        });
      }

      if (locationState?.sectionId && locationState?.createSession) {
        const section = state?.link?.sections?.find(
          (section: Section) => section.id === locationState.sectionId,
        );

        !!section && dispatch({ type: Types.SelectedCreateSession, payload: section });
      }

      location.state = {};
      window.history.replaceState({}, "");
    }
  }, [state?.form?.editedSessions?.blocks]);

  useEffect(() => {
    const responseData = resultSessionsCrud?.data;
    if (numberOfSavedSessions || numberOfSessionsToCreate || numberOfDeletedSessions) {
      const { numberOfEditedSessions, numberOfNewSessions } = (
        state?.form?.savedSessions ?? []
      ).reduce(
        (acc, session) => ({
          numberOfEditedSessions: acc.numberOfNewSessions + (session.isNew ? 0 : 1),
          numberOfNewSessions: acc.numberOfNewSessions + (session.isNew ? 1 : 0),
        }),
        { numberOfEditedSessions: 0, numberOfNewSessions: 0 },
      );

      let redirectToResultsView = false;

      const validateEdited = Boolean(numberOfEditedSessions > 0);
      const validateCreate = Boolean(numberOfNewSessions + state?.form?.sessionsToCreate.length);
      const validateDeleted = Boolean(responseData?.payload?.deletes?.length);

      if (
        (validateEdited || validateDeleted) &&
        (responseData?.payload?.updates ||
          responseData?.payload?.creates ||
          responseData?.payload?.deletes) &&
        !state?.result?.resultValidation
      ) {
        const { commited, skippedValidations, userCanSkipValidations } = responseData;
        dispatch({
          type: TypeResult.ResultValidation,
          payload: {
            sessionsPayload: [...responseData?.payload?.updates, ...responseData?.payload?.deletes],
            commited,
            skippedValidations,
            userCanSkipValidations,
          },
        });
      }

      if (validateCreate && responseData && !state?.result?.resultValidation) {
        const { commited, skippedValidations, userCanSkipValidations } = responseData;
        dispatch({
          type: TypeResult.CreateValidation,
          payload: {
            sessionsPayload: [...responseData?.payload?.creates],
            commited,
            skippedValidations,
            userCanSkipValidations,
          },
        });
      }

      if (responseData?.payload?.deletes?.length) {
        dispatch({
          type: TypeResult.DeleteValidation,
          payload: Object.values(state?.form?.sessionsToDelete),
        });
      }

      redirectToResultsView =
        (validateEdited && !!responseData && !validateCreate) ||
        (!validateEdited && validateCreate && !!responseData) ||
        (validateCreate && !!responseData && validateEdited) ||
        validateDeleted;

      if (redirectToResultsView) {
        dispatch({ type: TypeResult.SelectCreateSession, payload: null });
        dispatch({ type: TypeSwitch.SetLoading, payload: false });
        dispatch({ type: TypeSwitch.ResultsPage, payload: true });
      }
    }

    if (resultSessionsCrud?.error) setError(true);
  }, [resultSessionsCrud]);

  return (
    <>
      <AssignationActionsHeader
        locationCurrentSelectedWeek={currentSelectedWeek}
        onSubmitValidations={() => {
          dispatch({ type: TypeSwitch.SetLoading, payload: true });
          submitSessionsCrud();
        }}
        onShowValidationWarnings={setShowValidationWarning}
      />

      <section className={cx(css.content, css[`content__height${heightHeader}`])}>
        <aside className={cx(css.aside)}>
          <AccordionSections
            onCompareBySession={handleCompareSession}
            onCompareBySection={handleCompareBySection}
          />
        </aside>

        {state?.form?.selectedCreateSession && (
          <CreateSession warning={warning} setWarning={setWarning} />
        )}
        {!state?.form?.selectedCreateSession && <EditSession />}
      </section>

      <Modal
        typeState="warning"
        title={t("assignation-edit.form.notifications.sessions-without-assignation.title")}
        show={showValidationWarning}
        textButtonPrincipal={t(
          "assignation-edit.form.notifications.sessions-without-assignation.confirm",
        )}
        textButtonSecondary={t(
          "assignation-edit.form.notifications.sessions-without-assignation.cancel",
        )}
        onClickSecondary={() => setShowValidationWarning(false)}
        onClickPrincipal={() => {
          setShowValidationWarning(false);
          dispatch({ type: TypeSwitch.SetLoading, payload: true });
          submitSessionsCrud();
          dispatch({ type: Types.CleanUnsavableSessionsToCreate });
        }}
      >
        <Text>
          {t("assignation-edit.form.notifications.sessions-without-assignation.description")}
        </Text>
      </Modal>

      <Modal
        typeState="error"
        title={t("assignation-edit.form.notifications.generic-validation-error.title")}
        show={error}
        textButtonPrincipal={t(
          "assignation-edit.form.notifications.generic-validation-error.action",
        )}
        onClickPrincipal={() => window.location.reload()}
      >
        <Text>{t("assignation-edit.form.notifications.generic-validation-error.description")}</Text>
      </Modal>
    </>
  );
};

export default FormEdit;
